// ── Saved Journeys Panel ─────────────────────────────────────────────────────
//
// Lets a rep reload a previously saved "new customer" walkthrough (Account
// Setup fields + envAccounts + every stage's configuration) to validate it
// later, instead of it being lost once the tab/browser closes.
import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, FolderOpen, Loader2 } from 'lucide-react'
import { platformJourneysApi } from '@api/client'
import type { PlatformJourneyRecord, PlatformJourneySummary } from '@api/client'

interface SavedJourneysPanelProps {
  onLoad: (record: PlatformJourneyRecord) => void
  disabled?: boolean
  /** Bump to re-fetch the list (e.g. right after a new journey is created). */
  refreshKey?: number
}

export function SavedJourneysPanel({ onLoad, disabled, refreshKey }: SavedJourneysPanelProps) {
  const [journeys, setJourneys] = useState<PlatformJourneySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [loadingId, setLoadingId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void platformJourneysApi.list().then((res) => {
      if (cancelled) return
      if (res.success && res.data) setJourneys(res.data)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [refreshKey])

  async function handleLoad(id: number) {
    setLoadingId(id)
    const res = await platformJourneysApi.get(id)
    setLoadingId(null)
    if (res.success && res.data) onLoad(res.data)
  }

  if (!loading && journeys.length === 0) return null

  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-[#f8fafc]"
        style={{ background: '#ffffff' }}
      >
        {open ? <ChevronDown size={12} className="text-[#64748b] shrink-0" /> : <ChevronRight size={12} className="text-[#64748b] shrink-0" />}
        <FolderOpen size={12} className="text-[#4f46e5] shrink-0" />
        <span className="text-[11px] font-semibold text-[#1e293b] flex-1">
          Saved customers {!loading && `(${journeys.length})`}
        </span>
        {loading && <Loader2 size={11} className="animate-spin text-[#94a3b8]" />}
      </button>
      {open && (
        <div className="divide-y" style={{ borderTop: '1px solid #e5e7eb', borderColor: '#e5e7eb' }}>
          {journeys.map((j) => (
            <button
              key={j.id}
              onClick={() => void handleLoad(j.id)}
              disabled={disabled || loadingId === j.id}
              className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[#f8fafc] disabled:opacity-50"
            >
              <span className="text-[11px] font-medium text-[#1e293b] flex-1 truncate">{j.customerName}</span>
              <span className="text-[9px] font-mono uppercase text-[#94a3b8] shrink-0">{j.version}</span>
              <span className="text-[9px] text-[#94a3b8] shrink-0">{new Date(j.updatedAt).toLocaleDateString()}</span>
              {loadingId === j.id && <Loader2 size={11} className="animate-spin text-[#6366f1] shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
